import React, { useMemo } from 'react'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationLink,
  PaginationNext,
} from '@/components/ui/pagination'
import { useNavigate } from 'react-router-dom'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'

type Props = {
  pageSize?: number
  total?: number
  page: number
  pageQueryKey?: string
  onChange?: (page: number) => void
}

export const OffsetPagination = React.memo(
  ({
    pageSize = 1,
    total,
    page = 1,
    pageQueryKey = 'page',
    onChange,
  }: Props) => {
    const navigate = useNavigate()

    const link = (i: number) => {
      const nextState = new URL(location.href)
      if (onChange) {
        return nextState.pathname + nextState.search
      }

      nextState.searchParams.set(pageQueryKey, i.toString())
      return nextState.pathname + nextState.search
    }

    const totalPages = Math.ceil((total || 0) / pageSize)

    const groupSize = 10

    const pages = (() => {
      const groupStart = Math.floor((page - 1) / groupSize) * groupSize + 1
      const groupEnd = Math.min(groupStart + groupSize - 1, totalPages)

      const pages = Array.from(
        { length: groupEnd - groupStart + 1 },
        (_, i) => groupStart + i,
      )

      return pages
    })()

    const handleChange = (
      i: number,
      e: React.MouseEvent<HTMLAnchorElement>,
    ) => {
      e.preventDefault()
      onChange?.(i)
      navigate(link(i))
      const main = document.getElementById('main')
      main?.scrollTo({ top: 0 })
    }

    const totalPage = Math.ceil((total || 0) / pageSize)

    const hasNextPage = page < totalPage
    const hasPrevPage = page > 1

    const prevPagesEntry = page - 1
    const nextPagesEntry = page + 1

    if (!total) {
      return null
    }

    return (
      <Pagination className="mt-2 flex justify-center">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              isActive={false}
              onClick={(e) => handleChange(prevPagesEntry, e)}
              className={!hasPrevPage ? 'pointer-events-none opacity-50' : ''}
            />
          </PaginationItem>
          {pages.map((i) => {
            return (
              <PaginationItem key={i}>
                <PaginationLink
                  onClick={(e) => handleChange(i, e)}
                  isActive={page === i}
                  className="px-1"
                >
                  {i}
                </PaginationLink>
              </PaginationItem>
            )
          })}

          <PaginationItem>
            <PaginationNext
              className={!hasNextPage ? 'pointer-events-none opacity-50' : ''}
              onClick={(e) => hasNextPage && handleChange(nextPagesEntry, e)}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    )
  },
)
